import React from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@apollo/client/react';
import cn from 'clsx';
import { getOperationAST } from 'graphql/utilities';
import style from './style.module.scss';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import { TextAreaController } from '@components-clean/core/text-area';
import { useUnsavedChangesTracking, useUnsavedChangesConfirmation } from '@hooks-clean/index';
import { RiskReview } from '@interfaces/report-templates.interface';
import { IRefreshGridEventBusMessage, REFRESH_GRID } from '@pages-clean/risk-assessment/constants';
import { busDispatch } from '@utils-clean/eventBus';
import { useTranslation } from '@hooks/index';
import { ShowError } from '@utils/notification';
import {
    updateControlsHubChartConfig,
    updateControlsHubChartConfigVariables,
} from '@queries/generated/risk-assessment.generated';
import { GET_CM_CONTROLS_HUB_CHART_CONFIG, UPSERT_CM_CONTROLS_HUB_CHART_CONFIG } from '@queries/risk-assessment';
import { useDeleteRiskReview } from '@apis/bff-api/report-templates';

export interface DeleteRiskReviewDialogProps {
    onClose: () => void;
    onSubmit: () => void;
    riskReview: RiskReview;
}

type FormInputs = {
    deleteReason: string | null;
};

const COMPONENT_NAME = 'delete-risk-review-dialog';

const DeleteRiskReviewDialog = ({ onClose, onSubmit, riskReview }: DeleteRiskReviewDialogProps) => {
    const { t } = useTranslation('ram', { keyPrefix: 'manageRiskReviews.dialogs.deleteRiskReview' });
    const { t: tCommon } = useTranslation('common');
    const { deleteRiskReview } = useDeleteRiskReview();
    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            deleteReason: null,
        },
    });

    const [upsertControlsHubChartConfig] = useMutation<
        updateControlsHubChartConfig,
        updateControlsHubChartConfigVariables
    >(UPSERT_CM_CONTROLS_HUB_CHART_CONFIG);

    useUnsavedChangesTracking({
        area: 'risk-assessment-toolkit',
        componentName: COMPONENT_NAME,
        hasChanges: isDirty,
    });
    const confirmLossOfChanges = useUnsavedChangesConfirmation({
        area: 'risk-assessment-toolkit',
        mode: 'individual-components',
        componentNames: COMPONENT_NAME,
    });

    const onCloseHandler = async () => {
        const confirmed = await confirmLossOfChanges();
        if (!confirmed) {
            return;
        }
        onClose();
    };

    const onDelete = async (data: FormInputs) => {
        const response = await deleteRiskReview(riskReview.id!, data.deleteReason!);

        if (!Array.isArray(response) || !response[0]) {
            ShowError(tCommon('errorMessage'));
            return;
        }

        const operationName = getOperationAST(GET_CM_CONTROLS_HUB_CHART_CONFIG)?.name?.value;
        await upsertControlsHubChartConfig({
            variables: {
                studyId: riskReview.studyId,
            },
            refetchQueries: [operationName!],
        })
            .then(async (configResponse) => {
                if (!configResponse?.data || !configResponse?.data?.updateControlsHubChartConfig) {
                    return;
                }
                if (configResponse && configResponse.data?.updateControlsHubChartConfig) {
                    busDispatch<IRefreshGridEventBusMessage>(REFRESH_GRID, true);
                }
            })
            .catch(() => {});

        onSubmit();
    };

    const associatedReportWarning = (
        <span className={cn('k-text-left', style.fontSize)}>{t('associatedReportWarning')}</span>
    );

    return (
        <DraggableDialog
            dataTestId="delete-risk-review-dialog"
            onSubmit={!riskReview.inUse ? () => handleSubmit(onDelete)() : onClose}
            onClose={onCloseHandler}
            title={`${t('title')}: ${riskReview._itemId ?? riskReview.recordId}`}
            initialWidth={!riskReview.inUse ? 600 : 475}
            initialHeight={280}
            height={null}
            primaryBtnLabel={!riskReview.inUse ? tCommon('delete') : tCommon('close')}
            primaryBtnDisabled={!riskReview.inUse && (!isValid || !isDirty || isSubmitting)}
            secondaryBtnLabel={!riskReview.inUse ? undefined : ''}
        >
            <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                {riskReview.inUse && <InfoField type="Warning" message={associatedReportWarning} />}
                {!riskReview.inUse && (
                    <TextAreaController
                        className={style.textAreaOverrides}
                        maxLength={3000}
                        control={control}
                        name="deleteReason"
                        required
                        data-testid="field-risk-review-delete-reason"
                        label={`${t('deleteReasonLabel')} *`}
                    />
                )}
            </div>
        </DraggableDialog>
    );
};

export default DeleteRiskReviewDialog;
